import React from "react";
import { graphql, PageProps } from "gatsby";
import OpenGraph from "components/SEO/OpenGraph";
import { OfferPerPromoQuery } from "types/graphql-types";
import OfferPage from "./Offer";
import { offerPerPromoToLocalizedOffer } from "./utils";
import { useRemoteOffer } from "src/utils/useRemoteOffers";
import { useGeolocation } from "../../utils/localization/geolocation/GeolocationContext";
import { transformStateBuilder } from "../../utils/useFetchApi";
import { useLanguage } from "src/utils/localization/languages/I18nContext";

const OfferPerPromo: React.FC<PageProps<OfferPerPromoQuery>> = ({
  data,
  location
}) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const offer = data.offer!;

  const { countryCode } = useGeolocation();
  const language = useLanguage();
  const toLocalizedOfferResponse = transformStateBuilder(
    offerPerPromoToLocalizedOffer(offer, countryCode)
  );
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const offerResponse = useRemoteOffer(offer._id!, location, language);
  const localizedOfferResponse = toLocalizedOfferResponse(offerResponse);

  return (
    <React.Fragment>
      <OpenGraph
        openGraph={offer?.metadata?.openGraph}
        excludeFromSitemap={offer?.metadata?.excludeFromSitemap}
      />
      <OfferPage
        offer={offer}
        localizedOfferResponse={localizedOfferResponse}
        location={location}
      />
    </React.Fragment>
  );
};

export const query = graphql`
  query OfferPerPromo($id: String!) {
    offer: sanityExclusiveOfferPerPromo(id: { eq: $id }) {
      id
      _id
      metadata {
        excludeFromSitemap
        openGraph {
          ...OpenGraphMetaTags
        }

        tracking {
          name
        }
      }

      heroImage {
        asset {
          url
        }
        ...HeroResponsiveImageWithAlt
      }

      title {
        localized
      }

      description {
        localized
      }

      termsAndConditionsFootnote {
        ...FilterableMultilingualBlockContent
      }

      featureTitle {
        localized
      }

      features {
        ...ExclusiveOfferFeature
      }

      customCTALabel {
        localized
      }

      secondaryCTA {
        ...PageCTA
      }

      sections {
        ...PageBuilderSections
      }
    }
  }
`;

export default React.memo(OfferPerPromo);
